/**
 * useForceLogout — subscribes to the Identity hub `UpdateUser` event.
 * When the server signals that the current user has been logged in on
 * another device for the same app type ("Practice Management"), fires
 * `onForceLogout` so the caller can show the force-logout dialog.
 *
 * Flutter ref: `menu_container_screen.dart:238-271` (`initiateSignalR()`).
 *
 * Hub:      `/api/identity-server/NotificationsHub`
 * Method:   `UpdateUser`
 * Payload:  args[0] = `{ userId: string, appType: string }`
 *           (verbatim Flutter `UserSessionHandlingResp`)
 * Trigger:  args[0].userId === current user.userId
 *           AND args[0].appType === "Practice Management"
 *
 * Lifecycle mirrors `use-emirates-id-scan.ts` — StrictMode-safe via the
 * `disposed` guard so the cleanup-during-negotiation race doesn't throw.
 *
 * Note: this hook attaches a different SignalR connection from
 * `useEmiratesIdScan` even though both target the Identity service.
 * Emirates ID uses `/api/identity-server/NotificationsHub?username=<email>`
 * (a per-user channel); force-logout uses the bare hub URL. The backend
 * treats them as distinct WebSocket connections.
 */

import { useEffect } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { createHubConnection } from '@/shared/realtime/signalr-connection';
import { useAuthStore } from './auth-store';

const HUB_PATH = '/api/identity-server/NotificationsHub';

// Magic strings — verbatim from Flutter (menu_container_screen.dart:246, 252).
const SIGNALR_METHOD_UPDATE_USER = 'UpdateUser';
const APP_TYPE_PRACTICE_MANAGEMENT = 'Practice Management';

export interface ForceLogoutMessage {
  userId?: string | null;
  appType?: string | null;
}

export interface UseForceLogoutOptions {
  /** Called when the hub fires `UpdateUser` for the current user +
   *  PM app type. Caller is responsible for showing the dialog +
   *  triggering `useAuthStore.logout()` on user confirmation. */
  onForceLogout: () => void;
}

export function useForceLogout({ onForceLogout }: UseForceLogoutOptions): void {
  const userId = useAuthStore((s) => s.user?.userId);
  const status = useAuthStore((s) => s.status);

  useEffect(() => {
    // Gate: only connect when fully authenticated AND we know who we are.
    // Unauthenticated boot must not open a hub connection.
    if (status !== 'authenticated' || !userId) return;

    const getToken = () => useAuthStore.getState().getValidAccessToken();
    const hub = createHubConnection(HUB_PATH, getToken);

    hub.on(SIGNALR_METHOD_UPDATE_USER, (...args: unknown[]) => {
      const msg = unwrapForceLogoutFrame(args);
      if (!msg) return;
      // Samer review (PR #286, 2026-05-21): `.trim()` on the server-side
      // `appType` guards against the silent-skip risk of a stray
      // whitespace character (Flutter does an exact compare which would
      // miss such a message). Casing intentionally stays Flutter-verbatim
      // ("Practice Management") since the .NET server enum is fixed and
      // any case drift would be a server-side bug worth surfacing, not
      // silently absorbing.
      if (
        msg.userId === userId &&
        msg.appType?.trim() === APP_TYPE_PRACTICE_MANAGEMENT
      ) {
        // Samer review (PR #286, 2026-05-21): explicitly stop the hub
        // BEFORE firing the dialog. This pre-empts SignalR's
        // withAutomaticReconnect — if the server were to drop the
        // connection during the brief window between dialog-show and
        // `logout()`-driven dep-change cleanup, the reconnect could
        // re-trigger `UpdateUser`. Stopping here makes the subsequent
        // cleanup `hub.stop()` a no-op on an already-stopped connection.
        void hub.stop().catch(() => {});
        onForceLogout();
      }
    });

    // StrictMode-safe lifecycle — same pattern as useEmiratesIdScan.
    let disposed = false;

    void (async () => {
      try {
        if (hub.state === HubConnectionState.Disconnected) {
          await hub.start();
        }
        if (disposed && hub.state !== HubConnectionState.Disconnected) {
          await hub.stop();
        }
      } catch {
        // withAutomaticReconnect handles real failures; mid-negotiation
        // aborts in dev are silenced by the quiet logger.
      }
    })();

    return () => {
      // Samer review (PR #286, 2026-05-21): `disposed = true` is
      // assigned SYNCHRONOUSLY on the first line, before any async
      // hub.stop() call. In StrictMode's effect-double-invoke, the
      // start() IIFE checks `disposed && state !== Disconnected`
      // synchronously after its `await hub.start()` resolves —
      // because `disposed` flipped before stop() was even queued, the
      // IIFE sees the flag and stops the first hub itself. The second
      // mount creates a fresh hub with its own `disposed = false`,
      // so no double UpdateUser handler can fire.
      disposed = true;
      if (hub.state === HubConnectionState.Connected) {
        void hub.stop().catch(() => {});
      }
    };
    // onForceLogout intentionally omitted — capturing it would re-subscribe
    // on every render. Callers should keep it stable (useCallback) or accept
    // that the closure sees the handler from connection-time. The
    // setState-style caller in App.tsx is naturally stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, userId]);
}

/**
 * Flutter `UserSessionHandlingResp.fromJson(message.first)` reads
 * `{userId, appType}` from args[0]. JS SignalR's `connection.on(method, fn)`
 * spreads invoke args; `.NET` server calls `Clients.User(...).SendAsync(
 * "UpdateUser", payload)`, so args[0] is the payload object.
 */
function unwrapForceLogoutFrame(
  args: unknown[],
): ForceLogoutMessage | null {
  const head = args[0];
  if (!head || typeof head !== 'object') return null;
  return head as ForceLogoutMessage;
}
